import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Download, RefreshCcw, Check } from "lucide-react";

export default function SonarrImport() {
  const [series, setSeries] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/sonarr/series"); setSeries(data); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not load Sonarr series"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const importSelected = async () => {
    if (selected.size === 0) return;
    setImporting(true);
    try {
      const { data } = await api.post("/sonarr/import", { sonarr_ids: Array.from(selected) });
      toast.success(`Imported ${data.shows_imported} show(s), ${data.episodes_imported} episode(s)`);
      setSelected(new Set()); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const withEpisodes = series.filter((s) => s.episode_file_count > 0);

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24" data-testid="sonarr-import-page">
      <div className="px-6 md:px-12 max-w-[1500px] mx-auto">
        <span className="text-xs uppercase tracking-[0.3em] text-[#D9381E]">Sonarr</span>
        <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter text-white mt-3">Import TV Series</h1>
        <p className="text-[#8A8A8A] mt-4 max-w-2xl">
          {series.length === 0 && !loading ? "No series found — configure Sonarr in Settings first." : `${withEpisodes.length} series with episode files available.`}
        </p>

        <div className="mt-8 flex gap-3">
          <button onClick={load} disabled={loading} className="flex items-center gap-2 border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50" data-testid="sonarr-refresh">
            <RefreshCcw size={14} strokeWidth={1.5} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={importSelected} disabled={selected.size === 0 || importing} className="flex items-center gap-2 bg-[#D9381E] hover:bg-[#ED4B32] disabled:opacity-50 text-white px-5 py-2 text-xs uppercase tracking-[0.2em]" data-testid="sonarr-import-btn">
            <Download size={14} strokeWidth={1.5} /> Import {selected.size > 0 ? `(${selected.size})` : ""}
          </button>
        </div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {withEpisodes.map((m) => (
            <button key={m.sonarr_id} onClick={() => toggle(m.sonarr_id)}
              className={`relative aspect-[2/3] overflow-hidden bg-[#0F0F0F] border-2 transition-colors ${selected.has(m.sonarr_id) ? "border-[#D9381E]" : "border-transparent hover:border-white/20"}`}
              data-testid={`sonarr-card-${m.sonarr_id}`}>
              {m.poster_url ? <img src={m.poster_url} alt={m.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#8A8A8A] text-xs px-3 text-center">{m.title}</div>}
              <div className="absolute inset-x-0 bottom-0 bg-black/70 p-2">
                <p className="text-xs text-white truncate">{m.title}</p>
                <p className="text-[10px] text-[#8A8A8A]">{m.year} · {m.episode_file_count} eps</p>
              </div>
              {selected.has(m.sonarr_id) && (
                <div className="absolute top-2 right-2 w-7 h-7 bg-[#D9381E] flex items-center justify-center">
                  <Check size={14} strokeWidth={2} color="white" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
